import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  ChefHat,
  Loader2,
  Minus,
  Plus,
  ReceiptText,
  ShoppingBag,
  Trash2,
  UtensilsCrossed,
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

import GuestLayout from "@/components/guest/GuestLayout.jsx";
import { useSession } from "@/hooks/useSession.js";
import { guestFoodOrderApi } from "@/api/guest/guestFoodOrderApi";
import AddMenuItemModal, {
  buildCartItemKey,
} from "@/components/pos/AddMenuItemModal.jsx";
import PosBillItemBreakdown from "@/components/pos/PosBillItemBreakdown.jsx";
import {
  getOrderLineUnitPrice,
  getOrderLineTotal,
  buildOrderItemOptionSnapshot,
  buildOrderItemExtrasPayload,
} from "@/components/pos/orderCartUtils.js";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const categoryFallbackImages = [
  "https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1585937421612-70a008356fbe?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1541592106381-b31e9677c0e5?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1544145945-f90425340c7e?auto=format&fit=crop&w=900&q=80",
];

const foodFallbackImage =
  "https://images.unsplash.com/photo-1585937421612-70a008356fbe?auto=format&fit=crop&w=900&q=80";

const getApiData = (response) => {
  return response?.data?.data || response?.data?.Data || response?.data || null;
};

const getArrayFromResponse = (response, keys = []) => {
  const raw = getApiData(response);

  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.items)) return raw.items;
  if (Array.isArray(raw?.Items)) return raw.Items;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.Data)) return raw.Data;

  for (const key of keys) {
    if (Array.isArray(raw?.[key])) return raw[key];
  }

  return [];
};

const getApiMessage = (error, fallback = "Something went wrong.") => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.Message ||
    error?.response?.data?.title ||
    error?.message ||
    fallback
  );
};

const getCategoryId = (category) => {
  return (
    category?.id || category?.Id || category?.categoryId || category?.CategoryId
  );
};

const getCategoryName = (category) => {
  return (
    category?.name ||
    category?.Name ||
    category?.categoryName ||
    category?.CategoryName ||
    category?.title ||
    "Category"
  );
};

const getCategoryDescription = (category) => {
  return (
    category?.description ||
    category?.Description ||
    category?.details ||
    "Freshly prepared items from our kitchen."
  );
};

const getCategoryImage = (category, index = 0) => {
  return (
    category?.imageUrl ||
    category?.ImageUrl ||
    category?.image ||
    category?.Image ||
    category?.photoUrl ||
    category?.PhotoUrl ||
    categoryFallbackImages[index % categoryFallbackImages.length]
  );
};

const getCategoryItemCount = (category) => {
  return (
    category?.itemCount ||
    category?.ItemCount ||
    category?.menuCount ||
    category?.MenuCount ||
    category?.itemsCount ||
    category?.ItemsCount ||
    0
  );
};

const getMenuId = (menu) => {
  return menu?.id || menu?.Id || menu?.menuId || menu?.MenuId;
};

const getMenuName = (menu) => {
  return (
    menu?.itemName ||
    menu?.ItemName ||
    menu?.name ||
    menu?.Name ||
    menu?.menuName ||
    menu?.MenuName ||
    "Food Item"
  );
};

const getMenuPrice = (menu) => {
  return Number(
    menu?.itemPrice ??
      menu?.ItemPrice ??
      menu?.price ??
      menu?.Price ??
      menu?.menuPrice ??
      menu?.MenuPrice ??
      0,
  );
};

const getMenuDescription = (menu) => {
  return (
    menu?.description ||
    menu?.Description ||
    menu?.itemDescription ||
    menu?.ItemDescription ||
    "Freshly prepared by the kitchen."
  );
};

const getMenuImage = (menu) => {
  return (
    menu?.imageUrl ||
    menu?.ImageUrl ||
    menu?.image ||
    menu?.Image ||
    menu?.photoUrl ||
    menu?.PhotoUrl ||
    menu?.photo ||
    foodFallbackImage
  );
};

const getSessionId = (session) => {
  return (
    session?.id ||
    session?.Id ||
    session?.guestSessionId ||
    session?.GuestSessionId ||
    null
  );
};

const getRoomId = (room, session) => {
  return (
    room?.id ||
    room?.Id ||
    session?.roomId ||
    session?.RoomId ||
    session?.room?.id ||
    session?.Room?.Id ||
    null
  );
};

const round2 = (value) => Number(Number(value || 0).toFixed(2));

const getConfigSettings = (config) => {
  const raw = config?.settings || config?.Settings || config || {};

  return {
    serviceChargeEnabled:
      raw.serviceChargeEnabled ?? raw.ServiceChargeEnabled ?? false,
    serviceChargeRate: raw.serviceChargeRate ?? raw.ServiceChargeRate ?? 0,

    vatRateEnabled:
      raw.vatRateEnabled ??
      raw.VatRateEnabled ??
      raw.vatEnabled ??
      raw.VatEnabled ??
      false,
    vatRate: raw.vatRate ?? raw.VatRate ?? 0,

    tdsRateEnabled: raw.tdsRateEnabled ?? raw.TdsRateEnabled ?? false,
    tdsRate: raw.tdsRate ?? raw.TdsRate ?? 0,
  };
};

const calculateTotals = (items, config) => {
  const settings = getConfigSettings(config);

  const subTotal = items.reduce((sum, item) => {
    return sum + getOrderLineUnitPrice(item) * Number(item.quantity || 1);
  }, 0);

  const serviceChargeRate = Number(settings.serviceChargeRate || 0);
  const vatRate = Number(settings.vatRate || 0);
  const tdsRate = Number(settings.tdsRate || 0);

  const serviceChargeEnabled = settings.serviceChargeEnabled === true;
  const vatRateEnabled = settings.vatRateEnabled === true;
  const tdsRateEnabled = settings.tdsRateEnabled === true;

  const serviceCharge = serviceChargeEnabled
    ? (subTotal * serviceChargeRate) / 100
    : 0;

  const vat = vatRateEnabled ? ((subTotal + serviceCharge) * vatRate) / 100 : 0;
  const tds = tdsRateEnabled ? ((subTotal + serviceCharge) * tdsRate) / 100 : 0;

  const discount = 0;
  const total = subTotal + serviceCharge + vat + tds - discount;

  return {
    subTotal: round2(subTotal),
    serviceCharge: round2(serviceCharge),
    vat: round2(vat),
    tds: round2(tds),
    discount: round2(discount),
    total: round2(total),
    serviceChargeRate,
    vatRate,
    tdsRate,
    serviceChargeEnabled,
    vatRateEnabled,
    tdsRateEnabled,
  };
};

const getCookieValue = (name) => {
  if (typeof document === "undefined") return null;

  const value = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1];

  return value ? decodeURIComponent(value) : null;
};

const getGuestSessionFromCookie = () => {
  try {
    const rawGuestSession = getCookieValue("guestSession");

    if (!rawGuestSession) return null;

    return JSON.parse(rawGuestSession);
  } catch {
    return null;
  }
};

const formatShortOrderDate = (dateValue) => {
  const date = dateValue ? new Date(dateValue) : new Date();

  if (Number.isNaN(date.getTime())) {
    return new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const buildGuestOrderName = ({ session, room }) => {
  const cookieSession = getGuestSessionFromCookie();

  const activeSession = session || cookieSession || {};

  const roomNo =
    activeSession?.roomName ||
    activeSession?.RoomName ||
    activeSession?.room?.name ||
    activeSession?.Room?.Name ||
    room?.name ||
    room?.Name ||
    room?.roomNumber ||
    room?.RoomNumber ||
    "Room";

  const guestName =
    activeSession?.guestName || activeSession?.GuestName || "Guest";

  const checkInDate =
    activeSession?.checkInAt ||
    activeSession?.CheckInAt ||
    activeSession?.checkedInAt ||
    activeSession?.CheckedInAt ||
    new Date();

  return `${roomNo} - ${guestName} - ${formatShortOrderDate(checkInDate)}`;
};

const GuestFoodOrderPage = () => {
  const navigate = useNavigate();

  const { session, hotel, room, loading: sessionLoading } = useSession();

  const [loading, setLoading] = useState(true);
  const [menuLoading, setMenuLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [categories, setCategories] = useState([]);
  const [menus, setMenus] = useState([]);
  const [extras, setExtras] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [step, setStep] = useState("browse");

  const [customizeMenu, setCustomizeMenu] = useState(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);

  const defaultConfig = {
    settings: {
      serviceChargeEnabled: false,
      serviceChargeRate: 0,
      vatRateEnabled: false,
      vatRate: 0,
      tdsRateEnabled: false,
      tdsRate: 0,
    },
  };

  const [config] = useState(defaultConfig);

  const selectedItemCount = useMemo(
    () =>
      selectedItems.reduce((sum, item) => sum + Number(item.quantity || 1), 0),
    [selectedItems],
  );

  const totals = useMemo(
    () => calculateTotals(selectedItems, session || config),
    [selectedItems, session, config],
  );

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);

      const categoriesResponse = await guestFoodOrderApi.getCategories();

      setCategories(
        getArrayFromResponse(categoriesResponse, ["categories", "Categories"]),
      );

      try {
        const extrasResponse = await guestFoodOrderApi.getExtras();
        setExtras(getArrayFromResponse(extrasResponse, ["extras", "Extras"]));
      } catch {
        setExtras([]);
      }
    } catch (error) {
      toast.error(getApiMessage(error, "Failed to load food categories."));
      setCategories([]);
      setExtras([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!sessionLoading && !session) {
      navigate("/guest/expired", { replace: true });
      return;
    }

    if (!sessionLoading && session) {
      loadInitialData();
    }
  }, [sessionLoading, session, navigate, loadInitialData]);

  useEffect(() => {
    const loadMenus = async () => {
      if (!selectedCategory) {
        setMenus([]);
        return;
      }

      const categoryId = getCategoryId(selectedCategory);

      if (!categoryId) {
        toast.error("Invalid category selected.");
        setMenus([]);
        return;
      }

      try {
        setMenuLoading(true);

        const response = await guestFoodOrderApi.getMenusByCategory({
          categoryId,
          pageNumber: 1,
          pageSize: 100,
          isAscending: true,
          sortBy: "itemName",
        });

        setMenus(getArrayFromResponse(response, ["menus", "Menus"]));
      } catch (error) {
        toast.error(getApiMessage(error, "Failed to load food items."));
        setMenus([]);
      } finally {
        setMenuLoading(false);
      }
    };

    loadMenus();
  }, [selectedCategory]);

  const appendItemToCart = (menu, customization = {}) => {
    const menuId = customization.menuId || getMenuId(menu);

    if (!menuId) {
      toast.error("Invalid menu item.");
      return;
    }

    const quantity = Number(customization.quantity || 1);
    const orderItemMenuOptions = Array.isArray(customization.orderItemMenuOptions)
      ? customization.orderItemMenuOptions
      : [];
    const orderItemOption =
      customization.orderItemOption ||
      buildOrderItemOptionSnapshot(orderItemMenuOptions);
    const orderItemExtras = Array.isArray(customization.orderItemExtras)
      ? customization.orderItemExtras
      : [];
    const description =
      customization.description || getMenuDescription(menu) || "";
    const itemName = customization.itemName || getMenuName(menu);
    const itemPrice =
      customization.itemPrice ?? getMenuPrice(menu);
    const image = customization.image || getMenuImage(menu);
    const cartKey = buildCartItemKey({
      menuId,
      orderItemMenuOptions,
      orderItemOption,
      orderItemExtras,
    });

    setSelectedItems((prev) => {
      const existingIndex = prev.findIndex((item) => item.cartKey === cartKey);

      if (existingIndex >= 0) {
        return prev.map((item, index) =>
          index === existingIndex
            ? {
                ...item,
                quantity: Number(item.quantity || 1) + quantity,
              }
            : item,
        );
      }

      return [
        ...prev,
        {
          id:
            globalThis.crypto?.randomUUID?.() ||
            `${Date.now()}-${Math.random()}`,
          cartKey,
          itemName,
          itemPrice: Number(itemPrice || 0),
          menuId,
          orderId: null,
          quantity,
          description,
          image,
          orderItemMenuOptions,
          orderItemOption,
          orderItemExtras,
        },
      ];
    });

    toast.success(`${itemName} added`);
  };

  const addItem = (menu) => {
    setCustomizeMenu(menu);
    setCustomizeOpen(true);
  };

  const handleAddItemConfirm = (customization) => {
    if (!customizeMenu) return;

    appendItemToCart(customizeMenu, customization);
    setCustomizeMenu(null);
  };

  const increaseItem = (index) => {
    setSelectedItems((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index
          ? { ...item, quantity: Number(item.quantity || 1) + 1 }
          : item,
      ),
    );
  };

  const decreaseItem = (index) => {
    setSelectedItems((prev) =>
      prev
        .map((item, itemIndex) =>
          itemIndex === index
            ? { ...item, quantity: Number(item.quantity || 1) - 1 }
            : item,
        )
        .filter((item) => Number(item.quantity || 0) > 0),
    );
  };

  const removeItem = (index) => {
    setSelectedItems((prev) =>
      prev.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  const getLineTotal = (item) => round2(getOrderLineTotal(item));

  const submitOrder = async () => {
    if (selectedItems.length === 0) {
      toast.error("Please add at least one food item.");
      return;
    }

    try {
      setSubmitting(true);

      const orderName = buildGuestOrderName({
        session,
        room,
      });

      const payload = {
        orderName,

        status: "Open",

        subTotal: totals.subTotal,
        serviceCharge: totals.serviceCharge,
        vat: totals.vat,
        discount: totals.discount,
        total: totals.total,

        tdsRateEnabled: totals.tdsRateEnabled,
        tdsRate: totals.tdsRate,

        serviceChargeEnabled: totals.serviceChargeEnabled,
        serviceChargeRate: totals.serviceChargeRate,

        vatRateEnabled: totals.vatRateEnabled,
        vatRate: totals.vatRate,

        discountInPercentage: 0,
        paymentMethod: "Pay Later",

        orderItems: selectedItems.map((item) => ({
          id: null,
          itemName: item.itemName,
          itemPrice: Number(item.itemPrice || 0),
          menuId: item.menuId,
          orderId: null,
          quantity: Number(item.quantity || 1),
          description: item.description || "",
          // Menu options are persisted as individual OrderItemExtras rows
          // so each appears on its own priced bill line after reload.
          orderItemOption: null,
          orderItemExtras: buildOrderItemExtrasPayload(item),
        })),

        orderPayments: [],
      };

      const response = await guestFoodOrderApi.createOrder(payload);

      toast.success(
        response?.data?.message ||
          response?.data?.Message ||
          "Food order saved successfully",
      );

      setSelectedItems([]);
      setStep("success");
    } catch (error) {
      toast.error(getApiMessage(error, "Failed to save food order."));
    } finally {
      setSubmitting(false);
    }
  };

  if (sessionLoading || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!session) return null;

  return (
    <>
      <Helmet>
        <title>{`Food Order - ${hotel?.name || hotel?.Name || "InHimalayas Stay"}`}</title>
        <meta
          name="description"
          content="Order food from your room and pay later"
        />
      </Helmet>

      <GuestLayout hotel={hotel} room={room} session={session}>
        <style>
          {`
            .guest-food-flow,
            .guest-food-flow * {
              box-shadow: none !important;
            }

            .guest-food-flow .bg-card {
              background-color: rgba(255,255,255,0.75) !important;
              border-color: rgba(255,255,255,0.4) !important;
              backdrop-filter: blur(12px);
            }

            .guest-food-flow .bg-background {
              background-color: rgba(255,255,255,0.7) !important;
            }
          `}
        </style>

        <div
          className={`guest-food-flow mx-auto max-w-6xl space-y-6 ${
            step !== "success" && selectedItems.length > 0
              ? "pb-28 md:pb-0"
              : ""
          }`}
        >
          {step !== "success" && selectedItems.length > 0 && (
            <SelectedItemsBar
              selectedItems={selectedItems}
              selectedItemCount={selectedItemCount}
              getLineTotal={getLineTotal}
              increaseItem={increaseItem}
              decreaseItem={decreaseItem}
              removeItem={removeItem}
              totals={totals}
              onNext={() => setStep("review")}
            />
          )}

          {step === "success" ? (
            <SuccessState
              onNewOrder={() => {
                setStep("browse");
                setSelectedCategory(null);
                setSelectedItems([]);
                setMenus([]);
              }}
              onHome={() => navigate("/guest/home")}
            />
          ) : step === "review" ? (
            <BillReview
              selectedItems={selectedItems}
              totals={totals}
              getLineTotal={getLineTotal}
              onBack={() => setStep("browse")}
              onProceed={submitOrder}
              submitting={submitting}
            />
          ) : (
            <BrowseMenu
              categories={categories}
              menus={menus}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              addItem={addItem}
              menuLoading={menuLoading}
              onBackToCategories={() => {
                setSelectedCategory(null);
                setMenus([]);
              }}
            />
          )}
        </div>

        <AddMenuItemModal
          open={customizeOpen}
          onOpenChange={(open) => {
            setCustomizeOpen(open);
            if (!open) setCustomizeMenu(null);
          }}
          menu={customizeMenu}
          extras={extras}
          itemName={customizeMenu ? getMenuName(customizeMenu) : ""}
          itemPrice={customizeMenu ? getMenuPrice(customizeMenu) : 0}
          itemImage={customizeMenu ? getMenuImage(customizeMenu) : ""}
          onConfirm={handleAddItemConfirm}
        />
      </GuestLayout>
    </>
  );
};

const BrowseMenu = ({
  categories,
  menus,
  selectedCategory,
  setSelectedCategory,
  addItem,
  menuLoading,
  onBackToCategories,
}) => {
  if (!selectedCategory) {
    return (
      <section className="space-y-4">
        <div>
          <h2 className="mb-2 flex items-center gap-2 text-2xl font-bold text-white text-balance">
            <ChefHat className="h-6 w-6 text-white" />
            From Kitchen
          </h2>
        </div>

        {categories.length === 0 ? (
          <EmptyCard message="No food categories found." />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {categories.map((category, index) => (
              <motion.button
                type="button"
                key={getCategoryId(category) || index}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: index * 0.08 }}
                onClick={() => setSelectedCategory(category)}
                className="
                  group overflow-hidden rounded-3xl border border-white/40
                  bg-white/75 text-left backdrop-blur-md
                  transition active:scale-[0.98]
                  focus:outline-none focus:ring-2 focus:ring-white/50
                "
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={getCategoryImage(category, index)}
                    alt={getCategoryName(category)}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />

                  <div className="absolute bottom-3 left-3 right-3">
                    <p className="line-clamp-1 text-lg font-bold text-white">
                      {getCategoryName(category)}
                    </p>

                    <p className="mt-1 text-xs font-medium text-white/80">
                      {getCategoryItemCount(category)} items
                    </p>
                  </div>
                </div>

                <div className="p-4">
                  <p className="line-clamp-2 min-h-10 text-sm leading-relaxed text-slate-600">
                    {getCategoryDescription(category)}
                  </p>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm text-white/70">Selected category</p>

          <h2 className="text-2xl font-bold text-white">
            {getCategoryName(selectedCategory)}
          </h2>
        </div>

        <Button
          type="button"
          variant="outline"
          className="h-11 rounded-2xl border-white/40 bg-white/75 text-slate-800 hover:bg-white"
          onClick={onBackToCategories}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Categories
        </Button>
      </div>

      {menuLoading ? (
        <div className="flex min-h-[240px] items-center justify-center rounded-3xl border border-white/40 bg-white/75 backdrop-blur-md">
          <Loader2 className="h-9 w-9 animate-spin text-[#923536]" />
        </div>
      ) : menus.length === 0 ? (
        <EmptyCard message="No food items found in this category." />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {menus.map((menu, index) => (
            <FoodCard
              key={getMenuId(menu) || index}
              menu={menu}
              index={index}
              onAdd={() => addItem(menu)}
            />
          ))}
        </div>
      )}
    </section>
  );
};

const FoodCard = ({ menu, onAdd, index = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.06 }}
      className="h-full"
    >
      <Card className="h-full overflow-hidden rounded-3xl border border-white/40 bg-card">
        <div className="relative h-44 overflow-hidden bg-white/50">
          <img
            src={getMenuImage(menu)}
            alt={getMenuName(menu)}
            className="h-full w-full object-cover"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />

          <div className="absolute bottom-3 left-3 rounded-full bg-black/45 px-3 py-1 text-xs font-bold text-white backdrop-blur-md">
            Rs {getMenuPrice(menu).toFixed(2)}
          </div>
        </div>

        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="line-clamp-2 text-lg leading-snug text-slate-800">
              {getMenuName(menu)}
            </CardTitle>

            <div className="shrink-0 rounded-full bg-[#eadfdf]/90 px-3 py-1 text-sm font-bold text-[#923536]">
              Rs {getMenuPrice(menu).toFixed(2)}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <p className="line-clamp-2 min-h-10 text-sm leading-relaxed text-slate-600">
            {getMenuDescription(menu)}
          </p>

          <Button
            onClick={onAdd}
            className="h-11 w-full rounded-2xl bg-[#923536] hover:bg-[#7f2d2e]"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Item
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const SelectedItemsBar = ({
  selectedItems,
  selectedItemCount,
  getLineTotal,
  increaseItem,
  decreaseItem,
  removeItem,
  totals,
  onNext,
}) => {
  return (
    <Card className="fixed left-[max(0.75rem,env(safe-area-inset-left))] right-[max(0.75rem,env(safe-area-inset-right))] bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-40 rounded-2xl border border-white/40 bg-card md:sticky md:inset-auto md:top-3">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-slate-800">Your order</p>

            <p className="truncate text-sm text-slate-600">
              {selectedItemCount} item{selectedItemCount === 1 ? "" : "s"} · Rs{" "}
              {totals.total.toFixed(2)}
            </p>
          </div>

          <Button
            onClick={onNext}
            className="h-11 shrink-0 rounded-2xl bg-[#923536] px-4 hover:bg-[#7f2d2e]"
          >
            Review
            <ReceiptText className="ml-2 h-4 w-4" />
          </Button>
        </div>

        <div className="mt-4 hidden gap-3 overflow-x-auto pb-1 md:flex">
          {selectedItems.map((item, index) => (
            <div
              key={`${item.menuId}-${index}`}
              className="min-w-[260px] rounded-2xl border border-white/50 bg-white/70 p-3"
            >
              <div className="flex gap-3">
                <img
                  src={item.image || foodFallbackImage}
                  alt={item.itemName}
                  className="h-14 w-14 shrink-0 rounded-xl object-cover"
                />

                <div className="min-w-0 flex-1">
                  <div className="flex justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-800">
                        {item.itemName}
                      </p>

                      <PosBillItemBreakdown
                        item={item}
                        compact
                        className="mt-1 border-white/60 bg-white/60"
                      />

                      <p className="mt-1 text-xs text-slate-500">
                        Qty {item.quantity} · Line Rs {getLineTotal(item).toFixed(2)}
                      </p>
                    </div>

                    <Button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="shrink-0 text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <QuantityControl
                      quantity={item.quantity}
                      onDecrease={() => decreaseItem(index)}
                      onIncrease={() => increaseItem(index)}
                    />

                    <p className="text-sm font-bold text-slate-800">
                      Rs {getLineTotal(item).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const QuantityControl = ({ quantity, onDecrease, onIncrease }) => {
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        size="icon"
        variant="outline"
        className="h-8 w-8 rounded-xl border-white/50 bg-white/75"
        onClick={onDecrease}
      >
        <Minus className="h-3 w-3" />
      </Button>

      <span className="w-7 text-center text-sm font-semibold text-slate-800">
        {quantity}
      </span>

      <Button
        type="button"
        size="icon"
        variant="outline"
        className="h-8 w-8 rounded-xl border-white/50 bg-white/75"
        onClick={onIncrease}
      >
        <Plus className="h-3 w-3" />
      </Button>
    </div>
  );
};

const BillReview = ({
  selectedItems,
  totals,
  getLineTotal,
  onBack,
  onProceed,
  submitting,
}) => {
  return (
    <section className="mx-auto max-w-3xl">
      <Card className="rounded-3xl border border-white/40 bg-card">
        <CardHeader className="border-b border-white/40">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-2xl text-slate-800">
                <ReceiptText className="h-6 w-6 text-[#923536]" />
                Bill Summary
              </CardTitle>

              <p className="mt-1 text-sm text-slate-600">
                Review your items before saving this order.
              </p>
            </div>

            <Button
              variant="outline"
              onClick={onBack}
              className="h-11 rounded-2xl border-white/40 bg-white/75"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Add More
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-5 p-4 sm:p-5">
          <div className="space-y-3">
            {selectedItems.map((item, index) => (
              <div
                key={`${item.menuId}-${index}`}
                className="rounded-2xl border border-white/50 bg-white/70 p-4"
              >
                <div className="flex items-start gap-4">
                  <img
                    src={item.image || foodFallbackImage}
                    alt={item.itemName}
                    className="h-16 w-16 shrink-0 rounded-2xl object-cover"
                  />

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="line-clamp-1 font-semibold text-slate-800">
                          {item.itemName}
                        </p>

                        <p className="mt-1 text-sm text-slate-600">
                          Qty {item.quantity}
                        </p>

                        <PosBillItemBreakdown
                          item={item}
                          className="mt-2 border-white/60 bg-white/60"
                        />
                      </div>

                      <p className="shrink-0 font-bold text-slate-800">
                        Rs {getLineTotal(item).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-2 rounded-2xl border border-white/50 bg-white/70 p-4">
            <BillRow label="Sub Total" value={totals.subTotal} />

            {totals.serviceChargeEnabled && (
              <BillRow
                label={`Service Charge (${totals.serviceChargeRate}%)`}
                value={totals.serviceCharge}
              />
            )}

            {totals.vatRateEnabled && (
              <BillRow label={`VAT (${totals.vatRate}%)`} value={totals.vat} />
            )}

            {totals.tdsRateEnabled && (
              <BillRow label={`TDS (${totals.tdsRate}%)`} value={totals.tds} />
            )}

            <div className="border-t border-slate-200 pt-3">
              <BillRow label="Total" value={totals.total} strong />
            </div>
          </div>

          <Button
            onClick={onProceed}
            disabled={submitting}
            className="h-12 w-full rounded-2xl bg-[#923536] text-base hover:bg-[#7f2d2e]"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Saving Bill...
              </>
            ) : (
              <>
                Proceed and Save Bill
                <ShoppingBag className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </section>
  );
};

const BillRow = ({ label, value, strong = false }) => {
  return (
    <div
      className={`flex items-center justify-between gap-4 ${
        strong ? "text-lg font-bold text-slate-900" : "text-sm text-slate-600"
      }`}
    >
      <span>{label}</span>
      <span className="shrink-0">Rs {Number(value || 0).toFixed(2)}</span>
    </div>
  );
};

const EmptyCard = ({ message }) => {
  return (
    <Card className="rounded-3xl border border-white/40 bg-card">
      <CardContent className="p-8 text-center">
        <p className="text-slate-600">{message}</p>
      </CardContent>
    </Card>
  );
};

const SuccessState = ({ onNewOrder, onHome }) => {
  return (
    <Card className="mx-auto max-w-xl rounded-3xl border border-white/40 bg-card text-center">
      <CardContent className="space-y-5 p-8">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10 text-green-600">
          <CheckCircle2 className="h-9 w-9" />
        </div>

        <div>
          <h2 className="text-2xl font-bold text-slate-800">
            Food order saved
          </h2>

          <p className="mt-2 text-sm text-slate-600">
            Your bill has been saved. Payment can be completed later.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Button
            variant="outline"
            className="h-11 rounded-2xl border-white/40 bg-white/75"
            onClick={onNewOrder}
          >
            New Order
          </Button>

          <Button
            className="h-11 rounded-2xl bg-[#923536] hover:bg-[#7f2d2e]"
            onClick={onHome}
          >
            Back Home
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default GuestFoodOrderPage;
